import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { SaleDialogContent } from "./sale/SaleDialogContent";
import { SaleDialogFooter } from "./sale/SaleDialogFooter";
import { useSaleConfirmation } from "@/hooks/useSaleConfirmation";
import { PromotionHandler } from "./sale/PromotionHandler";
import { Promotion } from "@/utils/promotionUtils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItem: any;
  isGroupSale?: boolean;
  selectedProducts?: any[];
  onProductsChange?: (products: any[]) => void;
  refetch: () => void;
}

export const SaleDialog = ({
  open,
  onOpenChange,
  selectedItem,
  isGroupSale = false,
  selectedProducts = [],
  onProductsChange = () => {},
  refetch,
}: SaleDialogProps) => {
  const [saleQuantity, setSaleQuantity] = useState("");
  const [isCredit, setIsCredit] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [partialPayment, setPartialPayment] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [customerSignature, setCustomerSignature] = useState("");
  const [sellerSignature, setSellerSignature] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [activePromotion, setActivePromotion] = useState<Promotion | null>(null);

  const total = calculateTotal();

  const { isConfirming, handleConfirm } = useSaleConfirmation({
    selectedItem,
    isGroupSale,
    selectedProducts,
    onConfirm: handleSaleConfirm,
    onOpenChange,
    sellerSignature,
  });

  function calculateTotal() {
    if (isGroupSale) {
      return selectedProducts.reduce((sum, product) => {
        const basePrice = product.quantity * product.stock_item.data.price;
        if (activePromotion) {
          if (activePromotion.discount_type === 'percentage') {
            return sum + (basePrice * (1 - activePromotion.discount_value / 100));
          }
          return sum + (basePrice - activePromotion.discount_value);
        }
        return sum + basePrice;
      }, 0);
    } else if (selectedItem && saleQuantity) {
      const basePrice = parseInt(saleQuantity) * selectedItem.data.price;
      if (activePromotion) {
        if (activePromotion.discount_type === 'percentage') {
          return basePrice * (1 - activePromotion.discount_value / 100);
        }
        return basePrice - activePromotion.discount_value;
      }
      return basePrice;
    }
    return 0;
  }

  async function handleSaleConfirm() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté pour effectuer une vente");
        return;
      }

      if (isGroupSale) {
        for (const product of selectedProducts) {
          if (product.quantity > product.stock_item.quantity) {
            toast.error(`Stock insuffisant pour ${product.stock_item.data.name}`);
            return;
          }
        }
      } else {
        if (parseInt(saleQuantity) > selectedItem.quantity) {
          toast.error("Stock insuffisant");
          return;
        }
      }

      const saleData = {
        customer_name: customerName,
        customer_contact: customerContact,
        customer_address: customerAddress,
        payment_method: paymentMethod,
        is_credit: isCredit,
        partial_payment: isCredit ? parseFloat(partialPayment) : total,
        payment_due_date: isCredit ? paymentDate : null,
        total_amount: total,
        seller_signature: sellerSignature,
        customer_signature: customerSignature,
        customer_id: selectedCustomerId || null,
        promotion_id: activePromotion?.id || null,
        created_by: user.id,
      };

      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .insert(saleData)
        .select()
        .single();

      if (saleError) throw saleError;

      if (isGroupSale) {
        for (const product of selectedProducts) {
          await supabase
            .from('sale_items')
            .insert({
              sale_id: sale.id,
              stock_item_id: product.stock_item.id,
              quantity: product.quantity,
              unit_price: product.stock_item.data.price,
              total_price: product.quantity * product.stock_item.data.price,
            });

          await supabase
            .from('stock_items')
            .update({ quantity: product.stock_item.quantity - product.quantity })
            .eq('id', product.stock_item.id);
        }
      } else {
        await supabase
          .from('sale_items')
          .insert({
            sale_id: sale.id,
            stock_item_id: selectedItem.id,
            quantity: parseInt(saleQuantity),
            unit_price: selectedItem.data.price,
            total_price: parseInt(saleQuantity) * selectedItem.data.price,
          });

        await supabase
          .from('stock_items')
          .update({ quantity: selectedItem.quantity - parseInt(saleQuantity) })
          .eq('id', selectedItem.id);
      }

      toast.success("Vente effectuée avec succès");
      refetch();
      onOpenChange(false);
    } catch (error) {
      console.error("Erreur lors de la vente:", error);
      toast.error("Une erreur est survenue lors de la vente");
    }
  }

  useEffect(() => {
    if (!open) {
      setSaleQuantity("");
      setIsCredit(false);
      setCustomerName("");
      setCustomerContact("");
      setPartialPayment("");
      setPaymentDate("");
      setCustomerAddress("");
      setPaymentMethod("");
      setCustomerSignature("");
      setSellerSignature("");
      setSelectedCustomerId("");
      setActivePromotion(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        {selectedItem && (
          <PromotionHandler
            productId={selectedItem.id}
            selectedCustomerId={selectedCustomerId}
            onPromotionChange={setActivePromotion}
          />
        )}
        
        <SaleDialogContent
          isGroupSale={isGroupSale}
          selectedItem={selectedItem}
          saleQuantity={saleQuantity}
          onQuantityChange={setSaleQuantity}
          selectedProducts={selectedProducts}
          onProductsChange={onProductsChange}
          isCredit={isCredit}
          onCreditChange={setIsCredit}
          customerName={customerName}
          setCustomerName={setCustomerName}
          customerContact={customerContact}
          setCustomerContact={setCustomerContact}
          partialPayment={partialPayment}
          setPartialPayment={setPartialPayment}
          paymentDate={paymentDate}
          setPaymentDate={setPaymentDate}
          customerAddress={customerAddress}
          setCustomerAddress={setCustomerAddress}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          customerSignature={customerSignature}
          setCustomerSignature={setCustomerSignature}
          sellerSignature={sellerSignature}
          setSellerSignature={setSellerSignature}
          total={total}
          selectedCustomerId={selectedCustomerId}
          setSelectedCustomerId={setSelectedCustomerId}
        />

        <SaleDialogFooter
          onCancel={() => onOpenChange(false)}
          onConfirm={handleConfirm}
          isDisabled={isConfirming || !sellerSignature}
          isCredit={isCredit}
        />
      </DialogContent>
    </Dialog>
  );
};